import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import { useSelector } from "react-redux";
import {
  selectTestKitsGLIsCurrentlyOffering,
  selectKitsSelectedToBeOrdered
} from "store/reducers/order/OrderInformationSlice";
import Stepper from "components/v3/sharedComponents/Stepper";

export interface OrderKitStepperStepperProps {
  activeStepIndex: number;
  onBack?: (index: number) => undefined;
}

type Kit = {
  test_name: string;
  test_uuid: string;
};

type SubStep = {
  label: string;
  stepIndex: number;
};

type Step = {
  longName: string;
  onClick?: (params: { index: number; isComplete: boolean }) => undefined;
  shortName: string;
  stepIndex: number;
  subSteps: SubStep[];
};

const OrderKitStepper: React.FC<OrderKitStepperStepperProps> = ({
  activeStepIndex,
  onBack
}) => {
  const kitsGLCurrentOffers = useSelector(selectTestKitsGLIsCurrentlyOffering);
  const selectedKitsUuids = useSelector(selectKitsSelectedToBeOrdered);
  const selectedKits = useMemo(() => {
    return kitsGLCurrentOffers.filter((offer: Kit) =>
      selectedKitsUuids.includes(offer.test_uuid)
    );
  }, [kitsGLCurrentOffers, selectedKitsUuids]);
  const numSelectedKits = selectedKits?.length;
  const selectedKitsSubSteps = selectedKits.map((kit: Kit, index: number) => {
    const { test_name: testName } = kit;

    return {
      label: testName,
      onClick: () => {},
      stepIndex: index + 1
    };
  });

  const steps: Step[] = [
    {
      longName: "About your request",
      onClick: ({ index, isComplete }) => {
        if (isComplete) {
          onBack?.(index);
        }
      },
      shortName: "About your request",
      stepIndex: 0,
      subSteps: []
    },
    {
      longName: "Informed Consent",
      onClick: ({ index, isComplete }) => {
        if (isComplete) {
          onBack?.(index);
        }
      },
      shortName: "Informed Consent",
      stepIndex: 1,
      subSteps: selectedKitsSubSteps
    },
    {
      stepIndex: numSelectedKits + 2,
      longName: "Member Details",
      onClick: ({ index, isComplete }) => {
        if (isComplete) {
          onBack?.(index);
        }
      },
      shortName: "Member Details",
      subSteps: []
    },
    {
      stepIndex: numSelectedKits + 3,
      longName: "Confirmation",
      shortName: "Confirmation",
      subSteps: []
    }
  ];

  const renderGreeting = () => {
    return (
      <Box
        sx={{
          alignItems: {
            xs: "center",
            lg: "flex-start"
          },
          display: "flex",
          flexDirection: "column",
          gap: {
            xs: "5px",
            lg: 2
          },
          paddingTop: {
            xs: "30px",
            lg: 0
          },
          width: "100%"
        }}
      >
        <Typography variant="body_strong_xl">Request Kits</Typography>
        <Typography
          sx={{
            background: `linear-gradient(to right, ${COLORS.ACCENT3}, ${COLORS.PRIMARY})`,
            typography: {
              xs: "body_serif",
              lg: "body_lg_serif"
            },
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}
        >
          Let's confirm your request
        </Typography>
      </Box>
    );
  };

  return (
    <Stepper
      activeStepIndex={activeStepIndex}
      greeting={renderGreeting()}
      headerShown={true}
      showMobileGreeting={true}
      steps={steps}
    />
  );
};

export default OrderKitStepper;
